/**
 * Top-level route shell.
 *
 * Routes:
 *   /                        — screener landing (the marketing entry)
 *   /screener                — alias of `/`, present so external
 *                              links keep working when copy moves
 *                              the screener under its own path
 *   /sign-up                 — intake (FirebaseApp handles auth +
 *                              applicationId resolution +
 *                              screener-handoff hydration)
 *   /intake                  — alias for already-authenticated users
 *   /verify-email            — custom email-action handler. Firebase
 *                              Auth's "Customize action URL" for the
 *                              email-verification template points
 *                              here so users land in our branded UI
 *                              instead of `firebaseapp.com/__/auth/action`.
 *                              Reads oobCode + continueUrl from the
 *                              URL, calls `applyActionCode`, redirects.
 *   *                        — falls back to the screener landing
 *
 * The screener flow is a self-contained state machine
 * (`<ScreenerFlow />`) that decides which sub-screen to render. It
 * doesn't deep-link to specific questions because the flow is
 * linear and a deep-link to question N without prior answers
 * is meaningless.
 *
 * On the eligible outcome the screener navigates to `/sign-up`.
 * `<FirebaseApp />` then runs the existing auth gate; once the user
 * verifies and the intake mounts, the App's hydration effect picks
 * up the screener handoff snapshot from `localStorage` and seeds
 * the engine.
 */
import * as Sentry from '@sentry/react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';

import { VerifyEmailHandlerPage } from './auth/VerifyEmailHandlerPage';
import { FirebaseApp } from './firebase/FirebaseApp';
import { ScreenerFlow } from './screener/ScreenerFlow';

/**
 * Sentry-wrapped <Routes>. Pairs with `reactRouterV7BrowserTracing
 * Integration` in `sentry.ts` so navigation transactions are named
 * after the matched route pattern (e.g., `/intake`) rather than the
 * raw URL. When Sentry is no-op (no DSN), the wrapped Routes
 * behaves identically to the underlying component.
 */
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

export function AppRouter(): JSX.Element {
  return (
    <BrowserRouter>
      {/* WCAG 2.1 §2.4.1 — skip-to-main keyboard escape from any
          repeated header content. Visually hidden by default; the
          .skip-link class in styles/tokens.css reveals it on
          :focus. The target #main-content is the <main> element
          rendered by the active route. */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <SentryRoutes>
        <Route path="/" element={<ScreenerEntry />} />
        <Route path="/screener" element={<ScreenerEntry />} />
        <Route path="/sign-up" element={<FirebaseApp />} />
        <Route path="/sign-in" element={<FirebaseApp />} />
        <Route path="/intake" element={<FirebaseApp />} />
        <Route path="/verify-email" element={<VerifyEmailHandlerPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </SentryRoutes>
    </BrowserRouter>
  );
}

/**
 * Mounts the screener flow and wires its eligible-continue handler
 * to a router navigation toward `/sign-up`. The screener handoff
 * is already saved by `ScreenerFlow` before this fires; the intake
 * picks it up on mount.
 */
function ScreenerEntry(): JSX.Element {
  const navigate = useNavigate();
  return (
    <ScreenerFlow
      onEligibleContinue={() => {
        navigate('/sign-up');
      }}
      onSignInClick={() => {
        navigate('/sign-in');
      }}
    />
  );
}
